import React from 'react';
import {useTranslation} from 'react-i18next';

import type {AriaMenuItem, ButtonVariant, ColorScheme} from '@pexip/components';
import {
    AriaMenu,
    Button,
    FontVariant,
    Text,
    Tooltip,
    UserMenuButton,
} from '@pexip/components';

import {TestId} from '../../../test/testIds';

export type RegistrationMenuProps = {
    isSignedIn: boolean;
    isExpired: boolean;
    isOpen: boolean;
    displayName?: string;
    deviceAlias: string;
    variant?: ButtonVariant;
    buttonColorScheme: ColorScheme;
    onSignIn: () => void;
    onSignInAgain: () => void;
    onSignOut: () => void;
    onOpenChange: (isOpen: boolean) => void;
};

export const RegistrationMenu: React.FC<RegistrationMenuProps> = ({
    isSignedIn,
    isExpired,
    isOpen,
    displayName,
    deviceAlias,
    variant,
    buttonColorScheme,
    onSignIn,
    onSignInAgain,
    onSignOut,
    onOpenChange,
}) => {
    const {t} = useTranslation();

    if (!isSignedIn) {
        return (
            <Button
                data-testid={TestId.ButtonRegister}
                variant={variant}
                colorScheme={buttonColorScheme}
                size="large"
                onClick={onSignIn}
            >
                {t('register.sign-in', 'Sign in')}
            </Button>
        );
    }

    const triggerButtonProps: {
        'data-testid': string;
        'aria-label': string;
    } = {
        'data-testid': TestId.RegistrationMenuButton,
        'aria-label': t('register.menu-button-label', 'Toggle account menu'),
    };

    const items: AriaMenuItem[] = [
        ...(isExpired
            ? [
                  {
                      key: 'sign-in-again',
                      label: t('register.sign-in-again', 'Sign in again'),
                      testId: TestId.ButtonRegistrationSignInAgain,
                      onItemClick: onSignInAgain,
                  },
              ]
            : []),
        {
            key: 'sign-out',
            label: t('register.sign-out', 'Sign out'),
            testId: TestId.ButtonUnregister,
            isDanger: true,
            onItemClick: onSignOut,
        },
    ];

    return (
        <AriaMenu
            items={items}
            button={
                <UserMenuButton
                    {...triggerButtonProps}
                    isContentVisible={isOpen}
                    displayName={displayName || deviceAlias}
                    colorScheme={buttonColorScheme}
                    variant={variant}
                    modifier="square"
                />
            }
            renderButton={triggerButton => (
                <Tooltip
                    text={t('register.menu-button-tooltip', 'Account')}
                    position="bottomEnd"
                >
                    {triggerButton}
                </Tooltip>
            )}
            headerContent={
                <div className="d-flex flex-direction-column pt-4 pl-4 pr-4">
                    {displayName && (
                        <Text
                            colorScheme="light"
                            fontVariant={FontVariant.BodyBold}
                            variant="tinted"
                            data-testid={TestId.RegistrationMenuDisplayName}
                        >
                            {displayName}
                        </Text>
                    )}
                    <Text
                        colorScheme="light"
                        fontVariant={FontVariant.Body}
                        variant="tinted"
                        data-testid={TestId.RegistrationMenuDeviceAlias}
                    >
                        {deviceAlias}
                    </Text>
                    {isExpired && (
                        <Text
                            colorScheme="light"
                            fontVariant={FontVariant.Small}
                            variant="subtle"
                            data-testid={TestId.RegistrationMenuExpired}
                        >
                            {t('register.expired-title', 'Session expired')}
                        </Text>
                    )}
                </div>
            }
            testId={TestId.RegistrationMenu}
            popoverPlacement="bottom end"
            colorScheme="light"
            onOpenChange={onOpenChange}
        />
    );
};
